import { useState } from 'react';
import { toast } from 'sonner';
import { FileSpreadsheet, RotateCcw, Eye } from 'lucide-react';
import {
  usePersonForms,
  useSetPersonForm,
  useReopenPersonForm,
  usePersonFormDraft,
} from '../../hooks/onboardingOps';
import { CollapsibleSection } from '../ui/CollapsibleSection';
import { Button } from '../ui/Button';
import { Pill } from '../ui/Pill';
import { ApiError } from '../../lib/api';
import { formatDate } from '../../lib/dateFmt';
import { ItemStatusPill, canReopen } from './itemStatus';
import { FormAnswers } from './PersonFormSubmissions';
import type { FieldValue } from './formfields/formValues';
import type { AssignableForm } from '../../hooks/onboardingOps';

/** "3/8 fields · edited 12/06/2026" for a started-but-unsubmitted draft. */
function DraftProgress({ form }: { form: AssignableForm }) {
  if (!form.progress?.started) return null;
  const { required_done, required_total } = form.progress;
  const fields = required_total > 0 ? `${required_done}/${required_total} fields` : 'started';
  const edited = form.draft_updated_at ? ` · edited ${formatDate(form.draft_updated_at)}` : '';
  return <span className="text-xs text-warning-soft-fg">{fields}{edited}</span>;
}

/** Read-only view of what the inductee has typed into an in-progress form. */
function DraftAnswers({ personId, formId }: { personId: string; formId: string }) {
  const q = usePersonFormDraft(personId, formId, true);
  if (q.isLoading || !q.data) return <p className="text-xs text-fg-muted">Loading answers…</p>;
  return (
    <FormAnswers
      personId={personId}
      versionId={q.data.form_version_id}
      values={q.data.values as Record<string, FieldValue>}
      files={{}}
      consent={q.data.consent}
    />
  );
}

/**
 * Per-person form assignment. Forms are normally driven by the role template;
 * this lets HR assign a specific form to one person and reopen a submitted
 * one. Unpublished forms are shown but can't be assigned (the inductee would
 * have nothing to fill in).
 */
export function PersonFormsPanel({ personId }: { personId: string }) {
  const forms = usePersonForms(personId);
  const set = useSetPersonForm(personId);
  const reopen = useReopenPersonForm(personId);
  const [openId, setOpenId] = useState<string | null>(null);

  if (forms.isLoading || !forms.data) return null;
  if (forms.data.length === 0) return null;

  const assignedCount = forms.data.filter((f) => f.assigned).length;

  return (
    <CollapsibleSection
      icon={<FileSpreadsheet className="h-4 w-4 text-fg-muted" />}
      title="Forms to complete"
      trailing={`${assignedCount} assigned`}
    >
      <p className="mb-2 text-xs text-fg-muted">
        Tick the forms this person needs to fill in. Role templates pre-select some; adjust per person here.
      </p>
      <ul className="divide-y divide-divider/10">
        {forms.data.map((f) => (
          <li key={f.id} className="py-2.5 text-sm">
           <div className="flex items-center justify-between gap-2">
            <span className="flex min-w-0 items-center gap-2 text-fg">
              <span className="truncate">{f.title}</span>
              <ItemStatusPill status={f.status} />
              {!f.published ? <Pill tone="warning" size="sm">No version</Pill> : null}
              <DraftProgress form={f} />
            </span>
            <div className="flex shrink-0 items-center gap-3">
              {f.progress?.started ? (
                <Button
                  variant="ghost"
                  size="sm"
                  iconLeft={<Eye className="h-3.5 w-3.5" />}
                  onClick={() => setOpenId((cur) => (cur === f.id ? null : f.id))}
                >
                  {openId === f.id ? 'Hide' : 'View answers'}
                </Button>
              ) : null}
              {f.assigned && canReopen(f.status) ? (
                <Button
                  variant="ghost"
                  size="sm"
                  iconLeft={<RotateCcw className="h-3.5 w-3.5" />}
                  loading={reopen.isPending}
                  onClick={async () => {
                    try {
                      await reopen.mutateAsync(f.id);
                      toast.success('Reopened');
                    } catch (err) {
                      toast.error(err instanceof ApiError ? err.message : 'Could not reopen');
                    }
                  }}
                >
                  Reopen
                </Button>
              ) : null}
              <label className="flex items-center gap-2 text-fg-muted">
                <input
                  type="checkbox"
                  checked={f.assigned}
                  disabled={set.isPending || (!f.assigned && !f.published)}
                  onChange={async (e) => {
                    try {
                      await set.mutateAsync({ formId: f.id, assigned: e.target.checked });
                    } catch (err) {
                      toast.error(err instanceof ApiError ? err.message : 'Could not update');
                    }
                  }}
                />
                {f.assigned ? 'Assigned' : 'Not assigned'}
              </label>
            </div>
           </div>
           {openId === f.id ? (
             <div className="mt-2 rounded-token-sm border border-line/10 bg-surface-2 p-3">
               <DraftAnswers personId={personId} formId={f.id} />
             </div>
           ) : null}
          </li>
        ))}
      </ul>
    </CollapsibleSection>
  );
}
